// Render-props component that store state of CA step in local state and provides function to
// update it.
// Calls child function with the object with following fields:
// * stepState: current step state. This is an object `isSubmitting` and `isSaving` boolean
// properties. New props will be added in the future.
// * setStepState: function to update step. Works similar to `setState`, you can pass an object
// with supported state fields and it will be merged with existing state. For example,
// `setStepState({ isSaving: true })` will update isSaving and keep isSubmitting the same.
// It will find current Compound Assessment item by current userId, courseId and itemId.
// Example:
// <StepState stepId={stepId}>
//   {({ stepState, setStepState }) => (
//     <a onClick={() => setStepState({ isSubmitting: !stepState.isSubmitting })}>
//       { stepState.isSubmitting }
//     </a>
//   )}
// </StepState>
//
// This file previously used Apollo local state and was converted to use React context instead, in preparation for the
// Apollo v3 upgrade.
//
// TECH DEBT: This file essentially duplicates `StepStateContext.tsx`, but it was more straightforward to unblock the
// Apollo upgrade by changing this file's implementation versus updating all usages of it to use StepStateContext.
//
// The tech debt was deemed acceptable in part because all this code will be eventually deleted when it is replaced by
// `bundles/assignment-submission` as part of the Unify project.
import * as React from 'react';
import { createContext, useCallback, useMemo, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';

import type { WithUserCourseItemIdsProps } from 'bundles/compound-assessments/lib/withUserCourseItemIds';
import { UserCourseItemIds } from 'bundles/compound-assessments/lib/withUserCourseItemIds';

import type { StepStateKey, StepState as StepStateType } from './types';
import { DefaultState } from './types';

type RenderProps = {
  stepState: StepStateType;
  setStepState: (x0: Partial<StepStateType>) => void;
};

type Props = {
  stepId: string;
  courseId?: string;
  itemId?: string;
  userId?: number;
  children: (x0: RenderProps) => ReactNode;
};

type StepStateContextValue = {
  getStepState: (variables: StepStateKey) => StepStateType;
  updateStepState: (variables: StepStateKey, newState: StepStateType) => void;
};

export const createKey = ({ stepId, itemId, courseId, userId }: StepStateKey) =>
  `${stepId}-${itemId}-${courseId}-${userId}`;

const StepStateContext = createContext<StepStateContextValue>({
  getStepState: () => ({}),
  updateStepState: () => undefined,
});

const StepState = ({ children, stepId, courseId, userId, itemId }: Props) => (
  <UserCourseItemIds courseId={courseId} itemId={itemId} userId={userId}>
    {(ids: WithUserCourseItemIdsProps) => (
      <StepStateContext.Consumer>
        {({ getStepState, updateStepState }) => {
          const stepState = getStepState({ ...ids, stepId });
          return children({
            stepState,
            setStepState: (newState) => {
              updateStepState({ ...ids, stepId }, newState);
            },
          });
        }}
      </StepStateContext.Consumer>
    )}
  </UserCourseItemIds>
);

const QUIZ_STEP_ID = 'QUIZ_STEP';
export const QuizStepState = (props: {
  children: (x0: RenderProps) => ReactNode;
  itemId: string;
  courseId: string;
}) => <StepState {...props} stepId={QUIZ_STEP_ID} />;

export default StepState;

export const StepStateContextProvider = ({ children }: { children?: ReactNode }) => {
  const [stepState, setStepState] = useState(() => new Map<string, StepStateType>());

  const getStepState = useCallback(
    (variables: StepStateKey) => stepState.get(createKey(variables)) ?? DefaultState,
    [stepState]
  );

  const updateStepState = useCallback(
    (variables: StepStateKey, newState: StepStateType) =>
      setStepState((prevState) => {
        const key = createKey(variables);
        const updatedState = new Map(prevState);
        updatedState.set(key, { ...DefaultState, ...updatedState.get(key), ...newState });
        return updatedState;
      }),
    [setStepState]
  );

  const contextValue = useMemo<StepStateContextValue>(
    () => ({ getStepState, updateStepState }),
    [getStepState, updateStepState]
  );

  return <StepStateContext.Provider value={contextValue}>{children}</StepStateContext.Provider>;
};

export const withStepStateContextProvider = <BaseProps extends {}>(BaseComponent: ComponentType<BaseProps>) => {
  return (props: BaseProps) => (
    <StepStateContextProvider>
      <BaseComponent {...props} />
    </StepStateContextProvider>
  );
};
